import { Card,CardHeader,CardTitle,CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { X,Pencil,Trash2,MoreVertical,Printer as PrinterIcon,Star,Cpu,Network,MapPin,HardDrive,Clock,Info } from 'lucide-react'
import { DropdownMenu,DropdownMenuContent,DropdownMenuItem,DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import type { Printer } from '@/types'
import { PrinterTypeBadge } from './printer-type-badge'
import { PrinterStatusIndicator } from './printer-status-indicator'
import { formatDistanceToNow } from 'date-fns'
import { Tooltip,TooltipContent,TooltipProvider,TooltipTrigger } from '@/components/ui/tooltip'
import { formatInStoreTz } from '@/lib/time'

interface PrinterDetailCardProps {
  printer: Printer
  agentName?: string
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
  onTestPrint?: () => void
}

export function PrinterDetailCard({
  printer,
  agentName,
  onClose,
  onEdit,
  onDelete,
  onTestPrint,
}: PrinterDetailCardProps) {
  return (
    <Card className="squircle w-full md:w-[320px] lg:w-[360px] sticky top-0 max-h-[calc(100vh-6.5rem)] overflow-hidden animate-in slide-in-from-right-10 fade-in duration-300 shadow-lg pb-4 flex flex-col shrink-0">
      <CardHeader className="flex flex-row items-start justify-between pb-4 sticky top-0 bg-card z-10 space-y-0">
        <div className="flex flex-col gap-2">
          <div>
            <CardTitle className="text-base truncate flex items-center gap-2" title={printer.name}>
              <div className="flex items-center gap-1.5 shrink-0">
                {printer.isDefault && <Star className="h-3.5 w-3.5 fill-primary text-primary" />}
                <span>{printer.name}</span>
              </div>
              <PrinterStatusIndicator status={printer.status} isActive={printer.isActive} />
            </CardTitle>
          </div>
          <PrinterTypeBadge type={printer.type} />
        </div>
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onTestPrint && (
                <DropdownMenuItem onClick={onTestPrint}>
                  <PrinterIcon className="h-4 w-4" /> Test Print
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="h-4 w-4" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem className="text-destructive focus:text-destructive focus:bg-destructive/10" onClick={onDelete}>
                <Trash2 className="h-4 w-4" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="flex-1 overflow-y-auto pt-2 space-y-6">
        <div className="space-y-4">
          {/* Primary Connection Details */}
          <div className="bg-muted/30 border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Cpu className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Type</span>
              </div>
              <span className="text-xs font-medium">{printer.type}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Network className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Connection</span>
              </div>
              <span className="text-xs font-medium">{printer.connectionType}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Address</span>
              </div>
              <span className="text-xs font-mono truncate max-w-[150px]" title={printer.identifier || printer.address || ''}>
                {printer.identifier || printer.address || '—'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <HardDrive className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Agent</span>
              </div>
              <span className="text-xs font-medium truncate max-w-[150px]" title={agentName || printer.printAgent?.name || ''}>
                {agentName || printer.printAgent?.name || '—'}
              </span>
            </div>
          </div>

          {/* Hardware Specs */}
          <div className="bg-muted/30 border rounded-lg p-3 space-y-3">
            <div className="flex items-center justify-between font-mono gap-4">
              <div className="flex items-center gap-2 text-muted-foreground font-sans shrink-0">
                <Info className="h-3.5 w-3.5" />
                <span className="text-[10px] font-bold uppercase tracking-wider">Model</span>
              </div>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs truncate max-w-[180px] text-right cursor-help">
                      {(printer.make || printer.model) ? `${printer.make ?? ''} ${printer.model ?? ''}`.trim() : '—'}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-[280px] break-words">
                    <p className="text-xs">{(printer.make || printer.model) ? `${printer.make ?? ''} ${printer.model ?? ''}`.trim() : '—'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="w-3.5 h-3.5 flex items-center justify-center text-[10px] font-bold">SN</div>
                <span className="text-[10px] font-bold uppercase tracking-wider">Serial</span>
              </div>
              <span className="text-xs font-mono">{printer.serialNumber || '—'}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-muted-foreground text-[10px] font-bold uppercase tracking-wider">
                Res / Size
              </div>
              <span className="text-[11px] font-medium">
                {[
                  printer.dpi ? `${printer.dpi} DPI` : null,
                  printer.labelSize || (printer.labelWidthMm && printer.labelHeightMm ? `${printer.labelWidthMm}×${printer.labelHeightMm}mm` : null),
                  printer.paperWidth ? `${printer.paperWidth}mm` : null
                ].filter(Boolean).join(' • ') || '—'}
              </span>
            </div>

          </div>
        </div>

        <div className="pt-6 mt-auto border-t border-border/50 grid grid-cols-2 gap-4 text-xs text-muted-foreground">
          <div>
            <span className="font-medium text-foreground block mb-1">Created</span>
            {printer.createdAt ? formatInStoreTz(printer.createdAt, 'MMM d, yyyy') : '—'}
          </div>
          <div>
            <span className="font-medium text-foreground block mb-1">Updated</span>
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              {printer.updatedAt
                ? formatDistanceToNow(new Date(printer.updatedAt), { addSuffix: true })
                : printer.createdAt 
                  ? formatDistanceToNow(new Date(printer.createdAt), { addSuffix: true })
                  : '—'}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
